import { bookingPriceAud, bookingsByDepositId, depositMoneyInAud, heldMoneyAud, isDepositOnFile } from '@atink/domain';
import { useBrainStore } from '@/state/brainStore';
import { buildConfirmationWorklist } from '@/domain/confirmationWorklist';
import { dayKey } from '@/utils/studioDay';
import { isEndOfWorkNoRebook } from '@/domain/reportingEngine';
import type { Booking, Deposit, Piece, StudioId } from '@/domain/types';

/**
 * Money at risk this week (B10 / 14ym9bv75nd; Anas 1 Oct, Q1 + Q2). ONE selector
 * the Leak and Summary tabs read, so the headline is the sum of its parts by
 * construction:
 *   - confirmations: unconfirmed seats in the window — the Confirmations
 *     worklist's due rows (sequences 1–3 and reds) whose appointment is in it;
 *   - rebooks: sessions completed in the window that ended with no rebook — a
 *     finished piece and "no future booking" at the end of the work are not
 *     missed rebooks (B8 `isEndOfWorkNoRebook`);
 *   - deposits: money on file with no date, as of now (the shared on-file rule).
 * Each record is valued two ways (Q2 = both): the deposit money already taken
 * (ledger money in, $0 if none) and the seat value (the booked session price).
 * An on-file deposit has no seat yet, so its seat value is $0.
 * The parts are disjoint: a worklist seat is a live future booking, a rebook is a
 * completed session, and an on-file deposit has no booking at all.
 */

export type MoneyAtRiskPartKey = 'confirmations' | 'rebooks' | 'deposits';

export interface MoneyAtRiskRecord {
  id: string;
  customerId: string;
  /** Deposit money already taken on this record (ledger money in). */
  moneyAud: number;
  /** The booked session price (0 for a deposit with no seat). */
  seatAud: number;
}

export interface MoneyAtRiskPart {
  key: MoneyAtRiskPartKey;
  label: string;
  count: number;
  moneyAud: number;
  seatAud: number;
  records: MoneyAtRiskRecord[];
}

export interface MoneyAtRisk {
  parts: MoneyAtRiskPart[];
  totalCount: number;
  /** Σ parts' money taken. */
  totalMoneyAud: number;
  /** Σ parts' seat value. */
  totalSeatAud: number;
}

/**
 * A completed session that ended with no rebook and could have rebooked: not a
 * finished piece, and not "no future booking" at the end of the work (B8's
 * `isEndOfWorkNoRebook`: a one-off, or a completed piece).
 */
export function isMissedRebook(
  b: Booking,
  pieces: Record<string, Piece>,
  hasLiveFutureBooking: (customerId: string) => boolean,
): boolean {
  if (b.status !== 'completed' || !b.rebookOutcome) return false;
  const o = b.rebookOutcome;
  if (o.kind === 'rebookedInChair' || o.kind === 'pieceComplete') return false;
  if (isEndOfWorkNoRebook(b, pieces)) return false;
  // Same closures as the Leak chase list: sales resolved it, or the client has
  // booked since saying "later".
  if (o.kind === 'rebookDeferredToSales' && (o.resolvedOutcome === 'booked' || o.resolvedOutcome === 'declined')) return false;
  if (o.kind === 'willRebookLater' && hasLiveFutureBooking(b.customerId)) return false;
  return true;
}

/** Deposit money already taken on a record: ledger money in, whatever its state now. */
function moneyTakenAud(d: Deposit | undefined): number {
  return d ? depositMoneyInAud(d) : 0;
}

export function moneyAtRisk(
  days: readonly string[],
  studioId: StudioId | null | undefined,
  nowIso: string,
): MoneyAtRisk {
  const { bookings, deposits, confirmationCalls, artists, pieces } = useBrainStore.getState();
  const window = new Set(days);
  const inStudio = (sid: StudioId | undefined) => !studioId || sid === studioId;
  const byDeposit = bookingsByDepositId(bookings);
  const depositByBooking = new Map<string, Deposit>();
  for (const d of Object.values(deposits)) {
    for (const id of [d.bookingId, d.heldBookingId]) if (id && !depositByBooking.has(id)) depositByBooking.set(id, d);
  }
  const depositOf = (b: Booking): Deposit | undefined => (b.depositId ? deposits[b.depositId] : undefined) ?? depositByBooking.get(b.id);
  const now = Date.parse(nowIso);
  const liveFuture = new Set(
    Object.values(bookings)
      .filter((b) => !['cancelled', 'no-show', 'superseded'].includes(b.status) && Date.parse(b.start) >= now)
      .map((b) => b.customerId),
  );
  const record = (b: Booking): MoneyAtRiskRecord => ({
    id: b.id,
    customerId: b.customerId,
    moneyAud: moneyTakenAud(depositOf(b)),
    seatAud: bookingPriceAud(b, artists[b.artistId]),
  });

  const worklist = buildConfirmationWorklist({
    nowIso,
    bookings,
    confirmationCalls,
    studioIds: studioId ? [studioId] : [],
  });
  const confirmations = [...worklist.first, ...worklist.second, ...worklist.third, ...worklist.reds]
    // The studio day of the appointment (the worklist's own key is the browser's day).
    .filter((row) => window.has(dayKey(row.booking.start)))
    .map((row) => record(row.booking));

  // A session counts on its start day (7kv6), like every other report.
  const rebooks = Object.values(bookings)
    .filter((b) => inStudio(b.studioId) && isMissedRebook(b, pieces, (c) => liveFuture.has(c)) && window.has(dayKey(b.start)))
    .map(record);

  const onFile: MoneyAtRiskRecord[] = Object.values(deposits)
    .filter((d) => inStudio(d.studioId) && isDepositOnFile(d, bookings, byDeposit))
    .map((d) => ({ id: d.id, customerId: d.customerId, moneyAud: heldMoneyAud(d), seatAud: 0 }));

  const part = (key: MoneyAtRiskPartKey, label: string, records: MoneyAtRiskRecord[]): MoneyAtRiskPart => ({
    key,
    label,
    count: records.length,
    moneyAud: records.reduce((s, r) => s + r.moneyAud, 0),
    seatAud: records.reduce((s, r) => s + r.seatAud, 0),
    records,
  });
  const parts = [
    part('confirmations', 'Unconfirmed seats', confirmations),
    part('rebooks', 'Sessions with no rebook', rebooks),
    part('deposits', 'Deposits on file, no date', onFile),
  ];
  return {
    parts,
    totalCount: parts.reduce((s, p) => s + p.count, 0),
    totalMoneyAud: parts.reduce((s, p) => s + p.moneyAud, 0),
    totalSeatAud: parts.reduce((s, p) => s + p.seatAud, 0),
  };
}
